import { useEffect, useState } from "react";
import { Shield, AlertTriangle, CheckCircle2, XCircle, UserX } from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/AdminAuthContext";
import KPI from "./KPI";

const STATUSES = [
  { id: "open",       label: "Abiertos" },
  { id: "reviewing",  label: "En revisión" },
  { id: "resolved",   label: "Resueltos" },
  { id: "dismissed",  label: "Descartados" },
];

export default function ModerationTab() {
  const auth = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("open");
  const [reports, setReports] = useState([]);

  const load = () => {
    Promise.all([
      auth.apiGet("/admin/moderation/stats"),
      auth.apiGet(`/admin/reports?status=${filter}&limit=80`),
    ]).then(([s, r]) => {
      setStats(s.data);
      setReports(r.data);
    }).catch(() => toast.error("No se pudo cargar moderación"));
  };
  useEffect(load, [auth, filter]); // eslint-disable-line

  const update = async (id, status, banUserId) => {
    try {
      await auth.apiPatch(`/admin/reports/${id}`, { status, ban_user_id: banUserId || null });
      toast.success(banUserId ? "Reporte resuelto y usuario baneado" : `Reporte marcado como ${status}`);
      load();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  return (
    <div data-testid="tab-moderation" className="space-y-6">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">◊ Moderación de contenido</p>

      <section className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI testid="kpi-mod-open"      icon={AlertTriangle} k={stats?.open ?? "—"}      v="abiertos" highlight />
        <KPI testid="kpi-mod-reviewing" icon={Shield}        k={stats?.reviewing ?? "—"} v="en revisión" />
        <KPI testid="kpi-mod-resolved"  icon={CheckCircle2}  k={stats?.resolved ?? "—"}  v="resueltos" />
        <KPI testid="kpi-mod-dismissed" icon={XCircle}       k={stats?.dismissed ?? "—"} v="descartados" />
        <KPI testid="kpi-mod-banned"    icon={UserX}         k={stats?.banned_users ?? "—"} v="usuarios baneados" />
      </section>

      <div className="flex gap-1 text-xs" data-testid="mod-filter">
        {STATUSES.map((s) => (
          <button
            key={s.id}
            data-testid={`mod-filter-${s.id}`}
            onClick={() => setFilter(s.id)}
            className={`px-3 py-1 rounded-full font-mono ${filter === s.id ? "bg-ink text-cream" : "bg-cream-card hairline border"}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <section className="border hairline bg-cream-card rounded-2xl p-3 md:p-5">
        {reports.length === 0 ? (
          <p className="text-sm text-ink-mid text-center py-8" data-testid="reports-empty">Sin reportes en este estado.</p>
        ) : reports.map((r) => (
          <div key={r.id} data-testid={`report-${r.id}`} className="grid grid-cols-12 gap-3 px-3 py-3 border-b hairline last:border-b-0 items-center">
            <div className="col-span-12 md:col-span-2">
              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-coral/10 text-coral">
                {r.target_type}
              </span>
            </div>
            <div className="col-span-12 md:col-span-5 text-sm">{r.reason}</div>
            <div className="col-span-6 md:col-span-2 text-xs text-ink-mid font-mono truncate">{r.target_id}</div>
            <div className="col-span-6 md:col-span-3 flex justify-end gap-1.5">
              {filter === "open" && (
                <button data-testid={`report-review-${r.id}`} onClick={() => update(r.id, "reviewing")} className="text-[10px] px-2 py-1 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200">Revisar</button>
              )}
              {filter !== "resolved" && (
                <button data-testid={`report-resolve-${r.id}`} onClick={() => update(r.id, "resolved")} className="text-[10px] px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Resolver</button>
              )}
              {filter !== "dismissed" && (
                <button data-testid={`report-dismiss-${r.id}`} onClick={() => update(r.id, "dismissed")} className="text-[10px] px-2 py-1 rounded-full bg-zinc-200 text-zinc-700 hover:bg-zinc-300">Descartar</button>
              )}
              {r.target_type === "user" && filter === "open" && (
                <button data-testid={`report-ban-${r.id}`} onClick={() => update(r.id, "resolved", r.target_id)} className="text-[10px] px-2 py-1 rounded-full bg-red-100 text-red-700 hover:bg-red-200 inline-flex items-center gap-1">
                  <UserX className="w-3 h-3" /> Banear
                </button>
              )}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
